import {
  Offer,
  PolicyPlan,
  QuoteValues,
} from "@covertree/new-business-graphql";
import {
  Coverage,
  CoverageAccessor,
  getPackageCoveragesMap,
  PackageCoverageAccessor,
  ScreenWithCoverages,
  SettlementOption,
} from "@covertree/new-business-mappings";
import { formatCurrency, getMonthlyPrice } from "@covertree/utils";

export interface PersonalizedPlanDetailsDescription {
  name: string;
  description?: string;
  tooltip?: string;
  value?: string | number;
}

export type PersonalizedPlanDetailsType = {
  name: PolicyPlan;
  offer: Offer;
  quoteLocator: string;
  chosenPercentage: number;
  quote: QuoteValues;
  icon: string;
  isRecommended: boolean;
  monthlyPrice: number;
  yearlyPrice: number;
  title: string;
} & { [key in CoverageAccessor]?: PersonalizedPlanDetailsDescription };

export type CoveragesFlowData = {
  isVacantFlow: boolean;
  isSeasonalFlow: boolean;
  isTenantFlow: boolean;
  isRentalFlow: boolean;
  hasWaterDamage: boolean;
  hasWaterDamageDeductible: boolean;
};

const getOfferIcon = (offerPlan: PolicyPlan) => {
  switch (offerPlan) {
    case PolicyPlan.Silver:
      return "/images/plan-icons/silver.webp";
    case PolicyPlan.Gold:
    case PolicyPlan.CoverTree:
      return "/images/plan-icons/gold.webp";
    case PolicyPlan.Platinum:
      return "/images/plan-icons/platinum.webp";
  }
};

const getChosenPercentage = (offerPlan: PolicyPlan) => {
  switch (offerPlan) {
    case PolicyPlan.Silver:
      return 30;
    case PolicyPlan.Gold:
      return 55;
    case PolicyPlan.Platinum:
      return 15;
    default:
      return 0;
  }
};

export const getPlanTitle = (planName: PolicyPlan): string => {
  return `${planName.charAt(0).toUpperCase() + planName.slice(1)} Plan`;
};

export const getCoveragesData = ({
  isVacantFlow,
  isSeasonalFlow,
  isTenantFlow,
  isRentalFlow,
  hasWaterDamage,
  hasWaterDamageDeductible,
}: CoveragesFlowData): Coverage[] => {
  const packageCoveragesMap = getPackageCoveragesMap(
    ScreenWithCoverages.PlanDetails
  );

  const coveragesData: Coverage[] = [];

  if (!isTenantFlow) {
    coveragesData.push(
      packageCoveragesMap[PackageCoverageAccessor.HomeCoverage]
    );
  }

  coveragesData.push(
    packageCoveragesMap[PackageCoverageAccessor.YourBelongings]
  );

  if (isVacantFlow || isSeasonalFlow || isRentalFlow) {
    coveragesData.push(
      packageCoveragesMap[PackageCoverageAccessor.PremisesLiability]
    );
  }

  if (!isVacantFlow && !isSeasonalFlow) {
    coveragesData.push(
      packageCoveragesMap[PackageCoverageAccessor.PersonalLiability]
    );
  }

  coveragesData.push(
    packageCoveragesMap[PackageCoverageAccessor.StandardDeductible]
  );

  if (!isVacantFlow) {
    coveragesData.push(packageCoveragesMap[PackageCoverageAccessor.LossOfUse]);
  }

  if (!isTenantFlow) {
    coveragesData.push(
      packageCoveragesMap[PackageCoverageAccessor.OtherStructures]
    );
  }

  coveragesData.push(
    packageCoveragesMap[PackageCoverageAccessor.MedicalPayments]
  );

  if (!isTenantFlow) {
    coveragesData.push(packageCoveragesMap[PackageCoverageAccessor.WindHail]);
  }

  if (hasWaterDamage) {
    coveragesData.push(
      packageCoveragesMap[PackageCoverageAccessor.WaterDamage]
    );
  }

  if (hasWaterDamageDeductible) {
    coveragesData.push(
      packageCoveragesMap[PackageCoverageAccessor.WaterDamageDeductible]
    );
  }

  coveragesData.push(
    packageCoveragesMap[PackageCoverageAccessor.RoofPaymentSchedule]
  );

  return coveragesData;
};

export const createPersonalisedPlanWithDetails = (
  offer: Offer,
  isBookConversion = false
): PersonalizedPlanDetailsType => {
  const {
    plan,
    quote,
    pricing,
    quoteLocator,
    homeCoverageDescription,
    yourBelongingsDescription,
  } = offer;

  const packageCoveragesMap = getPackageCoveragesMap(
    ScreenWithCoverages.PlanDetails
  );

  const homeCoverageSettlmentType =
    packageCoveragesMap[PackageCoverageAccessor.HomeCoverageSettlementType];

  const yourBelongingsSettlementType =
    packageCoveragesMap[PackageCoverageAccessor.YourBelongingsSettlementType];

  const otherStructuresSettlementType =
    packageCoveragesMap[PackageCoverageAccessor.OtherStructuresSettlementType];

  const personalLiability =
    packageCoveragesMap[PackageCoverageAccessor.PersonalLiability];

  const premisesLiability =
    packageCoveragesMap[PackageCoverageAccessor.PremisesLiability];

  const standardDeductible =
    packageCoveragesMap[PackageCoverageAccessor.StandardDeductible];

  const lossOfUse = packageCoveragesMap[PackageCoverageAccessor.LossOfUse];

  const medicalPayments =
    packageCoveragesMap[PackageCoverageAccessor.MedicalPayments];

  const windHail = packageCoveragesMap[PackageCoverageAccessor.WindHail];

  const waterDamageDeductible =
    packageCoveragesMap[PackageCoverageAccessor.WaterDamageDeductible];

  const waterDamage = packageCoveragesMap[PackageCoverageAccessor.WaterDamage];

  const roofPaymentSchedule =
    packageCoveragesMap[PackageCoverageAccessor.RoofPaymentSchedule];

  return {
    name: plan,
    offer,
    quoteLocator,
    chosenPercentage: getChosenPercentage(plan),
    quote,
    icon: getOfferIcon(plan)!,
    isRecommended: plan === PolicyPlan.Gold && !isBookConversion,
    yearlyPrice: pricing.totalDue,
    monthlyPrice: getMonthlyPrice(pricing.totalDue),
    title: getPlanTitle(isBookConversion ? PolicyPlan.CoverTree : plan),
    [PackageCoverageAccessor.HomeCoverage]: {
      name: homeCoverageSettlmentType.getName(),
      description: homeCoverageDescription,
      tooltip: homeCoverageSettlmentType.getTooltip({
        settlementOption: homeCoverageDescription as SettlementOption,
      }),
      value: homeCoverageSettlmentType.getValueFormats(quote.homeCoverage)
        ?.value,
    },
    [PackageCoverageAccessor.YourBelongings]: {
      name: yourBelongingsSettlementType.getName(),
      description: yourBelongingsDescription,
      tooltip: yourBelongingsSettlementType.getTooltip({
        settlementOption: yourBelongingsDescription as SettlementOption,
      }),
      value: yourBelongingsSettlementType.getValueFormats(quote.yourBelongings)
        ?.value,
    },
    [PackageCoverageAccessor.PersonalLiability]: {
      name: personalLiability.getName(),
      value: personalLiability.getValueFormats(quote.personalLiability)?.value,
    },
    [PackageCoverageAccessor.PremisesLiability]: {
      name: premisesLiability.getName(),
      value: premisesLiability.getValueFormats(quote.premisesLiability)?.value,
    },
    [PackageCoverageAccessor.StandardDeductible]: {
      name: standardDeductible.getName(),
      value: standardDeductible.getValueFormats(quote.standardDeductible)
        ?.value,
    },
    [PackageCoverageAccessor.LossOfUse]: {
      name: lossOfUse.getName(),
      value: lossOfUse.getValueFormats(quote.lossOfUse)?.value,
    },
    [PackageCoverageAccessor.OtherStructures]: {
      name: otherStructuresSettlementType.getName(),
      value: otherStructuresSettlementType.getValueFormats(
        quote.otherStructures
      )?.value,
    },
    [PackageCoverageAccessor.MedicalPayments]: {
      name: medicalPayments.getName(),
      value: `${formatCurrency(quote.medicalPaymentToOther)} per person`,
      description: `${formatCurrency(25_000)} per incident`,
    },
    [PackageCoverageAccessor.WindHail]: {
      name: windHail.getName(),
      value: windHail.getValueFormats(quote.windHailDeductible)?.value,
    },
    [PackageCoverageAccessor.WaterDamageDeductible]: {
      name: waterDamageDeductible.getName(),
      value: waterDamageDeductible.getValueFormats(quote.waterDamageDeductible)
        ?.value,
    },
    [PackageCoverageAccessor.WaterDamage]: {
      name: waterDamage.getName(),
      value: quote.waterDamage,
    },
    [PackageCoverageAccessor.RoofPaymentSchedule]: {
      name: roofPaymentSchedule.getName(),
      value: quote.roofPaymentSchedule ? "Included" : "Not Included",
    },
  };
};
